import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star, Upload, Loader2, Check, ChevronLeft, ChevronRight, CheckCircle2, Pencil, AlertTriangle, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import {
  CONNECTION_TYPES, LANGUAGES, COUNTRIES, WORLD_COUNTRIES, POPULATIONS, APPROACHES, PAYMENT_METHODS,
  PROFESSIONAL_LINK_TYPES, WORKS_WITH, ENGAGEMENT_TYPES, GENDERS, PROFESSION_CATEGORIES, professionsByCategory, specialtySuggestions, specialtyLabel, isOtherProfession, localize, languageLabel, PROFESSIONS, professionConfig, countryLabel, COUNTRY_LABELS_EN, professionCategoryLabel, labelFor,
} from "../lib/meta";
import { useLanguageOptions, useLanguageLabeler, langActive, toggleLanguage } from "../lib/taxonomy";

const normalize = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const empty = {
  full_name: "", photo_path: null, phone: "", city: "", country: "", languages: [],
  profession: "", profession_category: "", profession_group: "", profession_other: "", credential_type: "", years_experience: null,
  works_with: [], engagement_types: [], countries_served: [], service_languages: [],
  country_of_origin: "", cultural_background: "", gender: "",
  title: "", license_number: "", license_org: "", license_country: "", areas: [], bio: "",
  populations_served: [], approach: "",
  service_format: "both", service_type: "both", availability: "",
  accepting_new_clients: false, payment_methods: [], works_with_insurance: false, insurance_accepted: "",
  professional_links: [], available_for_initiatives: false,
  connection_methods: [], primary_connection: null, show_phone_public: false,
  contact_form_enabled: false, contact_email: "",
  website: "", booking_url: "", video_url: "",
  qualifications: "", profession_details: {}, specialties: [],
};

// Which stage each required field lives on (for review guidance).
const FIELD_STAGE = {
  full_name: 0, country: 0, languages: 0,
  profession: 1, profession_other: 1, license_number: 1, license_org: 1, license_country: 1,
  bio: 2, years_experience: 2, expertise: 2, service_languages: 2,
  engagement_types: 3, populations_served: 2, who: 3, services: 3,
  contact_email: 5,
};

const Chips = ({ items, active, onToggle, testPrefix, lang, labelFn, isActive }) => (
  <div className="mt-2 flex flex-wrap gap-2">
    {items.map((it) => {
      const val = typeof it === "string" ? it : it.value;
      const label = labelFn ? labelFn(val) : (typeof it === "string" ? it : labelFor(it, lang));
      const on = isActive ? isActive(val) : active.includes(val);
      return (
        <button type="button" key={val} onClick={() => onToggle(val)} data-testid={`${testPrefix}-${val}`}
          className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70 hover:border-terracotta/40"}`}>
          {label}
        </button>
      );
    })}
  </div>
);

export function OnboardingWizard({ initial, startStage = 0 }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(() => {
    const merged = { ...empty, ...(initial || {}) };
    for (const k of Object.keys(empty)) {
      if (typeof empty[k] === "string" && merged[k] == null) merged[k] = "";
    }
    const cm = merged.connection_methods || [];
    const pick = (types) => (cm.find((c) => types.includes(c.type))?.url || "");
    merged.website = merged.website || pick(["website", "custom", "url"]);
    merged.booking_url = merged.booking_url || pick(["booking", "calendly", "scheduling", "google_calendar"]);
    merged.video_url = merged.video_url || pick(["video", "zoom", "google_meet", "meet"]);
    return merged;
  });
  const [stage, setStage] = useState(startStage);
  const [areas, setAreas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [submittedState, setSubmittedState] = useState(null); // 'submitted' | 'resubmitted'
  const [otherCountry, setOtherCountry] = useState("");
  const [otherOpen, setOtherOpen] = useState(false);
  const otherBlurRef = useRef(null);
  const addOtherCountry = (value) => {
    const c = (value != null ? value : otherCountry).trim();
    if (WORLD_COUNTRIES.includes(c) && !form.countries_served.includes(c)) {
      setForm((f) => ({ ...f, countries_served: [...f.countries_served, c] }));
    }
    setOtherCountry("");
  };
  const otherCountryOptions = WORLD_COUNTRIES.filter(
    (c) => !COUNTRIES.includes(c) && !form.countries_served.includes(c)
  ).filter((c) => {
    const q = normalize(otherCountry);
    if (!q) return true;
    return normalize(c).includes(q) || normalize(COUNTRY_LABELS_EN[c]).includes(q);
  });
  const fileRef = useRef();

  const isResubmit = initial && ["changes", "rejected"].includes(initial.status);
  const isEdit = initial && initial.status === "verified";

  const STAGES = [
    t("onboarding.stage_account"), t("onboarding.stage_identity"), t("onboarding.stage_experience"),
    t("onboarding.stage_services"), t("onboarding.stage_profile"), t("onboarding.stage_availability"),
    t("onboarding.stage_review"),
  ];

  useEffect(() => { api.get("/areas").then((r) => setAreas(r.data)).catch(() => {}); }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [stage]);
  useEffect(() => { if (user?.email && !form.contact_email) setForm((f) => ({ ...f, contact_email: user.email })); /* eslint-disable-next-line */ }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, val) => setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));
  const toggleLangArr = (k, code) => setForm((f) => ({ ...f, [k]: toggleLanguage(f[k], code) }));
  const langOptions = useLanguageOptions();
  const langLabel = useLanguageLabeler();
  const toggleDetail = (key, val) => setForm((f) => {
    const pd = { ...(f.profession_details || {}) };
    const arr = pd[key] || [];
    pd[key] = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
    return { ...f, profession_details: pd };
  });
  const pd = form.profession_details || {};
  const cfg = professionConfig(form.profession);

  const chooseCategory = (g) => setForm((f) => ({ ...f, profession_group: g, profession: "", profession_category: "" }));
  const chooseProfession = (v) => {
    const meta = PROFESSIONS.find((p) => p.value === v);
    setForm((f) => ({ ...f, profession: v, profession_category: meta?.category || "non_clinical", profession_group: meta?.group || f.profession_group }));
  };
  const [specialtyInput, setSpecialtyInput] = useState("");
  const addSpecialty = (s) => { const v = (s || "").trim(); if (!v) return; setForm((f) => (f.specialties.includes(v) ? f : { ...f, specialties: [...f.specialties, v] })); };
  const toggleSpecialty = (s) => setForm((f) => ({ ...f, specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s] }));
  const removeSpecialty = (s) => setForm((f) => ({ ...f, specialties: f.specialties.filter((x) => x !== s) }));
  const isClinical = form.profession_category === "clinical";

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("photo_path", data.path); }
    catch { toast.error("No se pudo subir la imagen"); }
    finally { setUploading(false); }
  };

  const addConnection = () => setForm((f) => ({ ...f, connection_methods: [...f.connection_methods, { type: "whatsapp", url: "", label: "" }] }));
  const updateConnection = (i, k, v) => setForm((f) => { const cm = [...f.connection_methods]; cm[i] = { ...cm[i], [k]: v }; return { ...f, connection_methods: cm }; });
  const removeConnection = (i) => setForm((f) => {
    const removed = f.connection_methods[i];
    const cm = f.connection_methods.filter((_, idx) => idx !== i);
    const primary = removed?.url === f.primary_connection ? (cm[0]?.url || null) : f.primary_connection;
    return { ...f, connection_methods: cm, primary_connection: primary };
  });
  const addLink = () => setForm((f) => ({ ...f, professional_links: [...f.professional_links, { type: "website", url: "" }] }));
  const updateLink = (i, k, v) => setForm((f) => { const ls = [...f.professional_links]; ls[i] = { ...ls[i], [k]: v }; return { ...f, professional_links: ls }; });
  const removeLink = (i) => setForm((f) => ({ ...f, professional_links: f.professional_links.filter((_, idx) => idx !== i) }));

  const buildPayload = (submit) => {
    const cmList = [];
    if (form.contact_email?.trim() && !form.contact_form_enabled) cmList.push({ type: "email", url: `mailto:${form.contact_email.trim()}` });
    if (form.phone?.trim()) cmList.push({ type: "phone", url: `tel:${form.phone.trim()}` });
    if (form.website?.trim()) cmList.push({ type: "website", url: form.website.trim() });
    if (form.booking_url?.trim()) cmList.push({ type: "booking", url: form.booking_url.trim() });
    if (form.video_url?.trim()) cmList.push({ type: "video", url: form.video_url.trim() });
    const { website, booking_url, video_url, ...rest } = form;
    return {
      ...rest, submit, onboarding_stage: String(stage),
      connection_methods: cmList, primary_connection: cmList[0]?.url || null,
      professional_links: form.professional_links.filter((c) => c.url?.trim()),
    };
  };

  // Client-side mirror of backend required-field rules (profession-aware).
  const missing = (() => {
    const m = [];
    if (!form.full_name.trim()) m.push("full_name");
    if (!form.profession) m.push("profession");
    if (isOtherProfession(form.profession) && !form.profession_other.trim()) m.push("profession_other");
    if (!form.country) m.push("country");
    if (!form.bio.trim()) m.push("bio");
    if (form.years_experience == null || form.years_experience === "") m.push("years_experience");
    if (!form.languages.length) m.push("languages");
    if (!form.contact_email.trim()) m.push("contact_email");
    if (isClinical) {
      for (const f of ["license_number", "license_org", "license_country"]) if (!form[f]?.trim()) m.push(f);
      if (!form.populations_served.length) m.push("populations_served");
      if (!form.engagement_types.length) m.push("engagement_types");
    } else {
      if (cfg.expertise && !(pd.expertise || []).length) m.push("expertise");
      if (cfg.who && !(pd.who || []).length) m.push("who");
      if (cfg.services && !(pd.services || []).length) m.push("services");
      if (form.profession === "language_teacher" && !form.service_languages.length) m.push("service_languages");
    }
    return m;
  })();

  const saveDraft = async (goTo) => {
    setBusy(true);
    try {
      await api.post("/psychologists", buildPayload(false));
      if (goTo === "exit") { toast.success(t("onboarding.draft_saved")); navigate("/mi-espacio"); return; }
      setStage(goTo);
    } catch { toast.error(t("onboarding.save_error")); }
    finally { setBusy(false); }
  };

  const doSubmit = async () => {
    setTriedSubmit(true);
    if (missing.length) { toast.error(t("onboarding.incomplete_title")); return; }
    setBusy(true);
    try {
      await api.post("/psychologists", buildPayload(true));
      if (isEdit) { toast.success(t("workspace.profile_saved")); navigate("/mi-espacio"); return; }
      setSubmittedState(isResubmit ? "resubmitted" : "submitted");
    } catch (e) {
      if (e.response?.status === 422) toast.error(t("onboarding.incomplete_title"));
      else toast.error(t("onboarding.save_error"));
    } finally { setBusy(false); }
  };

  if (submittedState) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="onb-submitted">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EBF6F0] text-[#1E6B47]"><CheckCircle2 className="h-8 w-8" /></div>
        <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-slateink">{t("onboarding.submitted_title")}</h1>
        <p className="mt-3 text-muted-foreground">{submittedState === "resubmitted" ? t("onboarding.resubmitted_desc") : t("onboarding.submitted_desc")}</p>
        <Button onClick={() => navigate("/mi-espacio")} className="mt-8 rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="onb-go-workspace">{t("onboarding.go_workspace")}</Button>
      </div>
    );
  }

  const photo = fileUrl(form.photo_path);
  const professionLabel = form.profession ? (isOtherProfession(form.profession) && form.profession_other ? form.profession_other : localize(PROFESSIONS, form.profession, lang)) : t("onboarding.not_provided");
  const loc = (opts, vals) => (vals || []).map((v) => localize(opts, v, lang)).join(", ");
  const missingLabel = (m) => {
    if (!isClinical) {
      if (m === "expertise" && cfg.expertise) return labelFor(cfg.expertise, lang);
      if (m === "who" && cfg.who) return labelFor(cfg.who, lang);
      if (m === "services" && cfg.services) return labelFor(cfg.services, lang);
    }
    return t(`onboarding.missing_${m}`);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8" data-testid="onboarding-wizard">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slateink">{t("onboarding.title")}</h1>
      <p className="mt-2 text-muted-foreground">{t("onboarding.subtitle")}</p>

      {/* Stepper */}
      <div className="mt-7">
        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>{t("onboarding.step_of").replace("{n}", stage + 1).replace("{total}", STAGES.length)}</span>
          <span className="font-medium text-slateink">{STAGES[stage]}</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-terracotta transition-all" style={{ width: `${((stage + 1) / STAGES.length) * 100}%` }} data-testid="onb-progress" />
        </div>
        <div className="mt-4 hidden flex-wrap gap-1.5 sm:flex">
          {STAGES.map((s, i) => (
            <button key={i} type="button" onClick={() => setStage(i)} data-testid={`onb-step-${i}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${i === stage ? "bg-slateink text-white" : i < stage ? "bg-terracotta-light text-terracotta" : "bg-secondary text-slateink/50"}`}>
              {i < stage && <Check className="h-3 w-3" />}{s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 space-y-6">
        {/* STAGE 0 — Cuenta */}
        {stage === 0 && (
          <section className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid="onb-stage-account">
            <h2 className="font-display text-xl font-semibold text-slateink">{t("onboarding.stage_account")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.account_intro")}</p>
            <div className="mt-6 space-y-5">
              <div className="flex items-center gap-5">
                <div className="h-20 w-20 overflow-hidden rounded-2xl bg-secondary">
                  {photo ? <img src={photo} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-slateink/30"><Upload className="h-6 w-6" /></div>}
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photo-btn">
                    {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    {form.photo_path ? t("join.change_photo") : t("join.upload_photo")}
                  </Button>
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div><Label>{t("common.name")} *</Label>
                  <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="input-full-name" className={triedSubmit && missing.includes("full_name") ? "border-destructive" : ""} />
                </div>
                <div><Label>{t("common.city")}</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="input-city" /></div>
                <div>
                  <Label>{t("common.country")} *</Label>
                  <Select value={form.country || ""} onValueChange={(v) => set("country", v)}>
                    <SelectTrigger data-testid="select-country" className={triedSubmit && missing.includes("country") ? "border-destructive" : ""}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{countryLabel(c, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t("common.language")} *</Label>
                <Chips items={langOptions.map((o) => o.value)} active={form.languages} isActive={(v) => langActive(form.languages, v)} onToggle={(v) => toggleLangArr("languages", v)} testPrefix="lang-chip" lang={lang} labelFn={(v) => langLabel(v, lang)} />
              </div>
            </div>
          </section>
        )}

        {/* STAGE 1 — Identidad profesional */}
        {stage === 1 && (
          <section className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid="onb-stage-identity">
            <h2 className="font-display text-xl font-semibold text-slateink">{t("onboarding.stage_identity")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.identity_intro")}</p>
            <div className="mt-6 space-y-6">
              <div>
                <Label className="text-base">{t("onboarding.category_label")} *</Label>
                <p className="text-sm text-muted-foreground">{t("onboarding.category_hint")}</p>
                <Select value={form.profession_group || ""} onValueChange={chooseCategory}>
                  <SelectTrigger className="mt-2" data-testid="onb-category-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{PROFESSION_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`onb-category-${c.value}`}>{professionCategoryLabel(c.value, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.profession_group && (
                <div>
                  <Label className="text-base">{t("onboarding.choose_profession")} *</Label>
                  <p className="text-sm text-muted-foreground">{t("onboarding.choose_profession_hint")}</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {professionsByCategory(form.profession_group).map((p) => {
                      const on = form.profession === p.value;
                      return (
                        <button type="button" key={p.value} onClick={() => chooseProfession(p.value)} data-testid={`onb-profession-${p.value}`}
                          className={`flex items-center justify-between gap-2 rounded-xl border-2 p-3 text-left text-sm font-medium transition-all ${on ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/80 hover:border-terracotta/40"}`}>
                          <span className="min-w-0 break-words">{localize(PROFESSIONS, p.value, lang)}</span>
                          {on && <Check className="h-4 w-4 shrink-0 text-terracotta" />}
                        </button>
                      );
                    })}
                  </div>
                  {form.profession && !isClinical && (
                    <p className="mt-2 text-xs text-muted-foreground">{t("onboarding.profession_public_note")}</p>
                  )}
                  {isOtherProfession(form.profession) && (
                    <div className="mt-3"><Label>{t("join.profession_other")} *</Label><Input value={form.profession_other} onChange={(e) => set("profession_other", e.target.value)} data-testid="input-profession-other" className={triedSubmit && missing.includes("profession_other") ? "border-destructive" : ""} /></div>
                  )}
                </div>
              )}
              <div><Label>{t("join.title_field")}</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="input-title" /></div>

              {isClinical && (
                <div className="rounded-xl border border-terracotta/40 bg-cream p-4">
                  <p className="font-semibold text-slateink">{t("onboarding.credentials")} *</p>
                  <p className="mb-3 text-xs text-muted-foreground">{t("onboarding.credentials_hint")}</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label>{t("profile.license")} *</Label><Input value={form.license_number} onChange={(e) => set("license_number", e.target.value)} data-testid="input-license" className={triedSubmit && missing.includes("license_number") ? "border-destructive" : ""} /></div>
                    <div><Label>{t("profile.license_org")} *</Label><Input value={form.license_org} onChange={(e) => set("license_org", e.target.value)} data-testid="input-license-org" className={triedSubmit && missing.includes("license_org") ? "border-destructive" : ""} /></div>
                    <div>
                      <Label>{t("profile.license_country")} *</Label>
                      <Select value={form.license_country || ""} onValueChange={(v) => set("license_country", v)}>
                        <SelectTrigger data-testid="select-license-country" className={triedSubmit && missing.includes("license_country") ? "border-destructive" : ""}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{countryLabel(c, lang)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>{t("onboarding.credential_type")}</Label><Input value={form.credential_type} onChange={(e) => set("credential_type", e.target.value)} data-testid="input-credential-type" /></div>
                  </div>
                </div>
              )}
              {!isClinical && form.profession && (
                <div data-testid="onb-qualifications-block">
                  <Label>{labelFor(cfg.qual, lang)}</Label>
                  <Textarea rows={3} value={form.qualifications} onChange={(e) => set("qualifications", e.target.value)} data-testid="input-qualifications"
                    placeholder={t("onboarding.qual_placeholder")} />
                  <p className="mt-1 text-xs text-muted-foreground">{t("onboarding.qual_note")}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* STAGE 2 — Experiencia y enfoque */}
        {stage === 2 && (
          <section className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid="onb-stage-experience">
            <h2 className="font-display text-xl font-semibold text-slateink">{t("onboarding.stage_experience")}</h2>
            <div className="mt-6 space-y-5">
              <div>
                <Label>{t("join.years_experience")} *</Label>
                <Input type="number" min="0" value={form.years_experience ?? ""} onChange={(e) => set("years_experience", e.target.value === "" ? null : Number(e.target.value))} data-testid="input-years-experience" className={`sm:max-w-[200px] ${triedSubmit && missing.includes("years_experience") ? "border-destructive" : ""}`} />
              </div>
              <div>
                <Label>{t("join.bio_field")} *</Label>
                <Textarea rows={5} value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder={t("join.bio_placeholder")} data-testid="input-bio" className={triedSubmit && missing.includes("bio") ? "border-destructive" : ""} />
              </div>
              {isClinical ? (
                <>
                  <div>
                    <Label>{t("join.areas_field")}</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {areas.map((a) => { const name = labelFor({ es: a.name_es, en: a.name_en }, lang); const on = form.areas.includes(name);
                        return <button type="button" key={a.id} onClick={() => toggleArr("areas", name)} data-testid={`area-chip-${a.id}`}
                          className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70 hover:border-terracotta/40"}`}>{name}</button>; })}
                    </div>
                  </div>
                  <div>
                    <Label>{t("join.populations_label")} *</Label>
                    <div className={`mt-1 rounded-lg ${triedSubmit && missing.includes("populations_served") ? "ring-1 ring-destructive p-2" : ""}`}>
                      <Chips items={POPULATIONS} active={form.populations_served} onToggle={(v) => toggleArr("populations_served", v)} testPrefix="population-chip" lang={lang} />
                    </div>
                  </div>
                  <div>
                    <Label>{t("join.approach_label")}</Label>
                    <Select value={form.approach || ""} onValueChange={(v) => set("approach", v)}>
                      <SelectTrigger data-testid="select-approach"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{APPROACHES.map((a) => <SelectItem key={a.value} value={a.value}>{localize(APPROACHES, a.value, lang)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  {cfg.expertise && (
                    <div>
                      <Label>{labelFor(cfg.expertise, lang)} *</Label>
                      <div className={`mt-1 rounded-lg ${triedSubmit && missing.includes("expertise") ? "ring-1 ring-destructive p-2" : ""}`}>
                        <Chips items={cfg.expertise.options} active={pd.expertise || []} onToggle={(v) => toggleDetail("expertise", v)} testPrefix="expertise" lang={lang} />
                      </div>
                    </div>
                  )}
                  {cfg.languagesTaught && (
                    <div>
                      <Label>{t("onboarding.languages_you_teach")} *</Label>
                      <div className={`mt-1 rounded-lg ${triedSubmit && missing.includes("service_languages") ? "ring-1 ring-destructive p-2" : ""}`}>
                        <Chips items={langOptions.map((o) => o.value)} active={form.service_languages} isActive={(v) => langActive(form.service_languages, v)} onToggle={(v) => toggleLangArr("service_languages", v)} testPrefix="taught-lang" lang={lang} labelFn={(v) => langLabel(v, lang)} />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div data-testid="onb-specialties-block">
                <Label>{t("onboarding.specialties_label")}</Label>
                <p className="text-sm text-muted-foreground">{t("onboarding.specialties_hint")}</p>
                {specialtySuggestions(form.profession, lang).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {specialtySuggestions(form.profession, lang).map(({ key, label }) => {
                      const on = (form.specialties || []).includes(key);
                      return <button type="button" key={key} onClick={() => toggleSpecialty(key)} data-testid={`specialty-suggest-${key}`}
                        className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-terracotta bg-terracotta-light text-terracotta" : "border-border text-slateink/70 hover:border-terracotta/40"}`}>{label}</button>;
                    })}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <Input value={specialtyInput} onChange={(e) => setSpecialtyInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSpecialty(specialtyInput); setSpecialtyInput(""); } }}
                    placeholder={t("onboarding.specialty_add_placeholder")} data-testid="specialty-input" />
                  <Button type="button" variant="outline" onClick={() => { addSpecialty(specialtyInput); setSpecialtyInput(""); }} data-testid="specialty-add-btn">{t("onboarding.add_btn")}</Button>
                </div>
                {(form.specialties || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {form.specialties.map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-sm text-secondary-foreground" data-testid={`specialty-chip-${s}`}>
                        {specialtyLabel(s, lang)}<button type="button" onClick={() => removeSpecialty(s)} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* STAGE 3 — Servicios y capacidades */}
        {stage === 3 && (
          <section className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid="onb-stage-services">
            <h2 className="font-display text-xl font-semibold text-slateink">{t("onboarding.stage_services")}</h2>
            <div className="mt-6 space-y-5">
              {isClinical ? (
                <>
                  <div><Label>{t("join.works_with")}</Label><Chips items={WORKS_WITH} active={form.works_with} onToggle={(v) => toggleArr("works_with", v)} testPrefix="works-with" lang={lang} /></div>
                  <div>
                    <Label>{t("join.engagement_types")} *</Label>
                    <div className={`mt-1 rounded-lg ${triedSubmit && missing.includes("engagement_types") ? "ring-1 ring-destructive p-2" : ""}`}>
                      <Chips items={ENGAGEMENT_TYPES} active={form.engagement_types} onToggle={(v) => toggleArr("engagement_types", v)} testPrefix="engagement" lang={lang} />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {cfg.who && (
                    <div>
                      <Label>{labelFor(cfg.who, lang)} *</Label>
                      <div className={`mt-1 rounded-lg ${triedSubmit && missing.includes("who") ? "ring-1 ring-destructive p-2" : ""}`}>
                        <Chips items={cfg.who.options} active={pd.who || []} onToggle={(v) => toggleDetail("who", v)} testPrefix="who" lang={lang} />
                      </div>
                    </div>
                  )}
                  {cfg.services && (
                    <div>
                      <Label>{labelFor(cfg.services, lang)} *</Label>
                      <div className={`mt-1 rounded-lg ${triedSubmit && missing.includes("services") ? "ring-1 ring-destructive p-2" : ""}`}>
                        <Chips items={cfg.services.options} active={pd.services || []} onToggle={(v) => toggleDetail("services", v)} testPrefix="services" lang={lang} />
                      </div>
                    </div>
                  )}
                  {(cfg.extras || []).map((ex) => (
                    <div key={ex.key}>
                      <Label>{labelFor(ex, lang)}</Label>
                      <Chips items={ex.options} active={pd[ex.key] || []} onToggle={(v) => toggleDetail(ex.key, v)} testPrefix={`detail-${ex.key}`} lang={lang} />
                    </div>
                  ))}
                </>
              )}
              <div>
                <Label>{t("join.format_label")}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["online", "in_person", "both"].map((v) => (
                    <button type="button" key={v} onClick={() => set("service_format", v)} data-testid={`format-${v}`}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${form.service_format === v ? "border-slateink bg-slateink text-white" : "border-border text-slateink/70"}`}>{t(`common.${v}`)}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("join.type_label")}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["paid", "volunteer", "both"].map((v) => (
                    <button type="button" key={v} onClick={() => set("service_type", v)} data-testid={`type-${v}`}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${form.service_type === v ? "border-slateink bg-slateink text-white" : "border-border text-slateink/70"}`}>{t(`common.${v}`)}</button>
                  ))}
                </div>
              </div>
              <div><Label>{t("join.payment_label")}</Label><Chips items={PAYMENT_METHODS} active={form.payment_methods} onToggle={(v) => toggleArr("payment_methods", v)} testPrefix="payment-chip" lang={lang} /></div>
              {isClinical && (
                <>
                  <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.works_with_insurance} onCheckedChange={(v) => set("works_with_insurance", !!v)} data-testid="checkbox-insurance" /><span className="text-sm text-slateink/80">{t("join.insurance_toggle")}</span></label>
                  {form.works_with_insurance && <Textarea rows={2} value={form.insurance_accepted} onChange={(e) => set("insurance_accepted", e.target.value)} placeholder={t("join.insurance_placeholder")} data-testid="input-insurance" />}
                </>
              )}
              <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.available_for_initiatives} onCheckedChange={(v) => set("available_for_initiatives", !!v)} data-testid="checkbox-initiatives" /><span className="text-sm text-slateink/80">{t("join.initiatives_label")}</span></label>
              <p className="rounded-lg bg-cream px-3 py-2.5 text-xs text-muted-foreground">{isClinical ? t("join.payment_disclaimer") : t("onboarding.payment_info_note")}</p>
            </div>
          </section>
        )}

        {/* STAGE 4 — Perfil */}
        {stage === 4 && (
          <section className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid="onb-stage-profile">
            <h2 className="font-display text-xl font-semibold text-slateink">{t("onboarding.stage_profile")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("join.fit_note")}</p>
            <div className="mt-6 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label>{t("join.gender")}</Label>
                  <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                    <SelectTrigger data-testid="select-gender"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{GENDERS.map((g) => <SelectItem key={g.value} value={g.value}>{localize(GENDERS, g.value, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("join.country_of_origin")}</Label><Input value={form.country_of_origin} onChange={(e) => set("country_of_origin", e.target.value)} data-testid="input-country-origin" /></div>
              </div>
              {!cfg.languagesTaught && (
                <div><Label>{t("join.service_languages")}</Label><Chips items={langOptions.map((o) => o.value)} active={form.service_languages} isActive={(v) => langActive(form.service_languages, v)} onToggle={(v) => toggleLangArr("service_languages", v)} testPrefix="service-lang" lang={lang} labelFn={(v) => langLabel(v, lang)} /></div>
              )}
              <div>
                <Label>{t("join.countries_served")}</Label>
                <Chips items={COUNTRIES} active={form.countries_served} onToggle={(v) => toggleArr("countries_served", v)} testPrefix="country-served" lang={lang} labelFn={(v) => countryLabel(v, lang)} />
                {form.countries_served.filter((c) => !COUNTRIES.includes(c)).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {form.countries_served.filter((c) => !COUNTRIES.includes(c)).map((c) => (
                      <button type="button" key={c} onClick={() => toggleArr("countries_served", c)} data-testid={`country-extra-${c}`}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-terracotta bg-terracotta-light px-3 py-1.5 text-sm text-terracotta">
                        <span className="truncate">{countryLabel(c, lang)}</span><X className="h-3 w-3 shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-3">
                  <Label className="text-xs font-medium text-muted-foreground">{t("onboarding.other_country_label")}</Label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={otherCountry}
                      onChange={(e) => { setOtherCountry(e.target.value); setOtherOpen(true); }}
                      onFocus={() => setOtherOpen(true)}
                      onBlur={() => { otherBlurRef.current = setTimeout(() => setOtherOpen(false), 150); }}
                      placeholder={t("onboarding.other_country_placeholder")}
                      data-testid="input-other-country"
                      autoComplete="off"
                      role="combobox"
                      aria-expanded={otherOpen}
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:border-terracotta focus:outline-none"
                    />
                    {otherOpen && (
                      <div
                        data-testid="other-country-list"
                        className="mt-1 max-h-56 w-full overflow-y-auto overscroll-contain rounded-lg border border-border bg-white shadow-sm"
                      >
                        {otherCountryOptions.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground" data-testid="other-country-empty">
                            {t("onboarding.no_matches")}
                          </div>
                        ) : (
                          otherCountryOptions.map((c) => (
                            <button
                              type="button"
                              key={c}
                              data-testid={`other-country-option-${c}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                if (otherBlurRef.current) clearTimeout(otherBlurRef.current);
                                addOtherCountry(c);
                                setOtherOpen(true);
                              }}
                              className="block w-full truncate px-3 py-2 text-left text-sm text-slateink hover:bg-cream focus:bg-cream focus:outline-none"
                            >
                              {countryLabel(c, lang)}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{t("onboarding.other_country_hint")}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* STAGE 5 — Disponibilidad */}
        {stage === 5 && (
          <section className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid="onb-stage-availability">
            <h2 className="font-display text-xl font-semibold text-slateink">{t("onboarding.stage_availability")}</h2>
            <div className="mt-6 space-y-5">
              <div className={`rounded-xl ${triedSubmit && missing.includes("contact_email") ? "ring-1 ring-destructive p-3" : ""}`}>
                <p className="font-semibold text-slateink">{t("onboarding.contact_title")}</p>
                <div className="mt-3 space-y-4">
                  <div>
                    <Label>{t("onboarding.contact_email_label")} *</Label>
                    <Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="input-contact-email" placeholder="tu@email.com" className={triedSubmit && missing.includes("contact_email") ? "border-destructive" : ""} />
                    <p className="mt-1 text-xs text-muted-foreground">{t("onboarding.contact_email_hint")}</p>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.contact_form_enabled} onCheckedChange={(v) => set("contact_form_enabled", !!v)} data-testid="checkbox-contact-form" /><span className="text-sm text-slateink/80">{t("onboarding.use_contact_form")}</span></label>
                  <div>
                    <Label>{t("onboarding.phone_label")}</Label>
                    <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone-contact" placeholder="+57 300 123 4567" />
                    <p className="mt-1 text-xs text-muted-foreground">{t("onboarding.phone_hint")}</p>
                    <label className="mt-3 flex items-start gap-3 cursor-pointer">
                      <Checkbox checked={form.show_phone_public} onCheckedChange={(v) => set("show_phone_public", !!v)} data-testid="checkbox-show-phone-public" />
                      <span className="text-sm text-slateink/80">{t("onboarding.phone_public_label")}
                        <span className="block text-xs text-muted-foreground">{t("onboarding.phone_public_hint")}</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <p className="font-semibold text-slateink">{t("onboarding.links_title")}</p>
                <div className="mt-3 space-y-4">
                  <div><Label>{t("onboarding.website_label")}</Label><Input type="url" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="input-website" placeholder="https://…" /></div>
                  <div><Label>{t("onboarding.booking_label")}</Label><Input type="url" value={form.booking_url} onChange={(e) => set("booking_url", e.target.value)} data-testid="input-booking" placeholder="https://…" /><p className="mt-1 text-xs text-muted-foreground">{t("onboarding.booking_hint")}</p></div>
                  <div><Label>{t("onboarding.video_label")}</Label><Input type="url" value={form.video_url} onChange={(e) => set("video_url", e.target.value)} data-testid="input-video" placeholder="https://…" /></div>
                </div>
              </div>
              <div>
                <Label>{t("onboarding.availability_notes_label")}</Label>
                <Textarea rows={3} value={form.availability} onChange={(e) => set("availability", e.target.value)} data-testid="input-availability" placeholder={t("onboarding.availability_notes_hint")} />
              </div>
              <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.accepting_new_clients} onCheckedChange={(v) => set("accepting_new_clients", !!v)} data-testid="checkbox-accepting" /><span className="text-sm text-slateink/80">{t("join.accepting_label")}</span></label>
            </div>
          </section>
        )}

        {/* STAGE 6 — Revisión */}
        {stage === 6 && (
          <section className="space-y-4" data-testid="onb-stage-review">
            <div className="rounded-2xl border border-border bg-white p-6 sm:p-8">
              <h2 className="font-display text-xl font-semibold text-slateink">{t("onboarding.review_title")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("onboarding.review_intro")}</p>
            </div>

            {triedSubmit && missing.length > 0 && (
              <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-5" data-testid="onb-missing">
                <p className="flex items-center gap-2 font-semibold text-destructive"><AlertTriangle className="h-4 w-4" />{t("onboarding.incomplete_title")}</p>
                <p className="mt-1 text-sm text-slateink/70">{t("onboarding.incomplete_desc")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {missing.map((m) => (
                    <button key={m} type="button" onClick={() => setStage(FIELD_STAGE[m] ?? 0)} className="rounded-full bg-white border border-destructive/40 px-3 py-1 text-xs text-destructive" data-testid={`onb-missing-${m}`}>
                      {missingLabel(m)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <ReviewCard title={t("onboarding.stage_account")} stageIndex={0} onEdit={setStage} t={t} rows={[
              [t("common.name"), form.full_name], [t("common.phone"), form.phone], [t("common.city"), form.city],
              [t("common.country"), countryLabel(form.country, lang)], [t("common.language"), form.languages.map((l) => langLabel(l, lang)).join(", ")],
            ]} />
            <ReviewCard title={t("onboarding.stage_identity")} stageIndex={1} onEdit={setStage} t={t} rows={[
              [t("join.profession"), professionLabel + (form.profession ? ` · ${isClinical ? t("join.tag_clinical") : t("join.tag_non_clinical")}` : "")],
              form.profession_other ? [t("join.profession_other"), form.profession_other] : null,
              [t("join.title_field"), form.title],
              ...(isClinical
                ? [[t("profile.license"), form.license_number], [t("profile.license_org"), form.license_org], [t("profile.license_country"), countryLabel(form.license_country, lang)]]
                : [[labelFor(cfg.qual, lang), form.qualifications]]),
            ].filter(Boolean)} />
            <ReviewCard title={t("onboarding.stage_experience")} stageIndex={2} onEdit={setStage} t={t} rows={(isClinical
              ? [[t("join.years_experience"), form.years_experience == null ? "" : String(form.years_experience)],
                 [t("join.bio_field"), form.bio], [t("join.areas_field"), form.areas.join(", ")],
                 [t("join.populations_label"), loc(POPULATIONS, form.populations_served)],
                 [t("join.approach_label"), form.approach ? localize(APPROACHES, form.approach, lang) : ""]]
              : [[t("join.years_experience"), form.years_experience == null ? "" : String(form.years_experience)],
                 [t("join.bio_field"), form.bio],
                 ...(cfg.expertise ? [[labelFor(cfg.expertise, lang), loc(cfg.expertise.options, pd.expertise)]] : []),
                 ...(cfg.languagesTaught ? [[t("onboarding.languages_you_teach"), form.service_languages.map((l) => langLabel(l, lang)).join(", ")]] : []),
                 [t("profile.specialties"), (form.specialties || []).map((s) => specialtyLabel(s, lang)).join(", ")]])} />
            <ReviewCard title={t("onboarding.stage_services")} stageIndex={3} onEdit={setStage} t={t} rows={(isClinical
              ? [[t("join.works_with"), loc(WORKS_WITH, form.works_with)],
                 [t("join.engagement_types"), loc(ENGAGEMENT_TYPES, form.engagement_types)]]
              : [...(cfg.who ? [[labelFor(cfg.who, lang), loc(cfg.who.options, pd.who)]] : []),
                 ...(cfg.services ? [[labelFor(cfg.services, lang), loc(cfg.services.options, pd.services)]] : []),
                 ...(cfg.extras || []).map((ex) => [labelFor(ex, lang), loc(ex.options, pd[ex.key])])]
              ).concat([
                [t("join.format_label"), t(`common.${form.service_format}`)], [t("join.type_label"), t(`common.${form.service_type}`)],
                [t("join.payment_label"), loc(PAYMENT_METHODS, form.payment_methods)],
              ])} />
            <ReviewCard title={t("onboarding.stage_profile")} stageIndex={4} onEdit={setStage} t={t} rows={[
              [t("join.gender"), form.gender ? localize(GENDERS, form.gender, lang) : ""],
              [t("join.service_languages"), form.service_languages.map((l) => langLabel(l, lang)).join(", ")],
              [t("join.countries_served"), form.countries_served.map((c) => countryLabel(c, lang)).join(", ")],
            ]} />
            <ReviewCard title={t("onboarding.stage_availability")} stageIndex={5} onEdit={setStage} t={t} rows={[
              [t("onboarding.contact_email_label"), form.contact_email],
              [t("onboarding.phone_label"), form.phone],
              [t("onboarding.website_label"), form.website],
              [t("onboarding.booking_label"), form.booking_url],
              [t("onboarding.video_label"), form.video_url],
              [t("join.accepting_label"), form.accepting_new_clients ? "✓" : "—"],
              [t("onboarding.availability_notes_label"), form.availability],
            ]} />

            <div className="flex justify-end pt-2">
              <Button size="lg" onClick={doSubmit} disabled={busy} className="rounded-full bg-terracotta hover:bg-terracotta-hover px-8" data-testid="onb-submit">
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common.saving")}</> : (isEdit ? t("common.save") : isResubmit ? t("onboarding.resubmit") : t("onboarding.submit"))}
              </Button>
            </div>
          </section>
        )}
      </div>

      {/* Footer nav (hidden on review since it has its own submit) */}
      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="ghost" onClick={() => setStage((s) => Math.max(0, s - 1))} disabled={stage === 0 || busy} data-testid="onb-back" className="w-full justify-center sm:w-auto"><ChevronLeft className="mr-1 h-4 w-4" />{t("onboarding.back")}</Button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button variant="outline" onClick={() => saveDraft("exit")} disabled={busy} className="w-full rounded-full sm:w-auto" data-testid="onb-save-exit">{t("onboarding.save_exit")}</Button>
          {stage < 6 && (
            <Button onClick={() => saveDraft(stage + 1)} disabled={busy} className="w-full rounded-full bg-slateink hover:bg-slateink/90 sm:w-auto" data-testid="onb-next">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("onboarding.save_continue")}<ChevronRight className="ml-1 h-4 w-4" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewCard({ title, stageIndex, onEdit, rows, t }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6" data-testid={`onb-review-${stageIndex}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold text-slateink">{title}</h3>
        <Button variant="ghost" size="sm" onClick={() => onEdit(stageIndex)} data-testid={`onb-edit-${stageIndex}`}><Pencil className="mr-1.5 h-3.5 w-3.5" />{t("common.edit")}</Button>
      </div>
      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map(([label, value], i) => (
          <div key={i} className="text-sm">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="text-slateink">{value && String(value).trim() ? value : <span className="text-slateink/40">{t("onboarding.not_provided")}</span>}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
